import ServiceClient, {
  defaultIsOk,
  FetchWithRetryOptions,
} from "src/utils/extension/ServiceClient";
import {
  RateLimitGate,
  IntegrationRateLimitWaitSchedule,
} from "src/utils/extension/rateLimitGate";
import {
  getDepopCookies,
  getUserIdFromCookies,
} from "src/utils/extension/depop";

const DepopApiBase = "https://webapi.depop.com";

export class NotLoggedError extends Error {
  constructor() {
    super("You must be logged in Depop to perform the sync process.");
  }
}

export class UnauthorizedError extends Error {
  url: string;
  status: number;
  json: object;

  constructor(operation: string, url: string, status: number, json: object) {
    super(`(depop) ${operation} Failed (unauthorized error property)`);
    this.url = url;
    this.status = status;
    this.json = json;
  }
}

class DepopClient extends ServiceClient {
  private accessTokenCache?: string;
  private rateLimitGate = new RateLimitGate();

  async fetchWithRetry(
    url: string,
    opts: RequestInit = {},
    {
      retries = IntegrationRateLimitWaitSchedule.length,
      isOk = defaultIsOk,
    }: FetchWithRetryOptions = {}
  ): Promise<Awaited<ReturnType<typeof this.fetch>>> {
    return await this.rateLimitGate.fetchWithRateLimitGate(
      (u, o) => this.fetch(u, o),
      url,
      opts,
      {
        retries,
        waitSchedule: IntegrationRateLimitWaitSchedule,
        isOk,
        label: "depop",
      }
    );
  }

  async getAccessToken(): Promise<string> {
    if (this.accessTokenCache) return this.accessTokenCache;
    const cookies = await getDepopCookies(this.extensionId);
    const token = cookies.find((c) => c.name === "access_token")?.value;
    if (!token) throw new NotLoggedError();
    this.accessTokenCache = token;
    return token;
  }

  async getUserIdFromCookie(): Promise<string> {
    const cookies = await getDepopCookies(this.extensionId);
    const userId = getUserIdFromCookies(cookies);
    if (!userId) throw new NotLoggedError();
    return userId;
  }

  private async request(operation: string, url: string) {
    const accessToken = await this.getAccessToken();
    const res = await this.fetchWithRetry(url, {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
      method: "GET",
      credentials: "omit",
    });

    if (res.status === 401 || res.status === 403) {
      throw new UnauthorizedError(operation, url, res.status, res.data);
    }

    if (!res.ok) {
      console.error(`(depop) ${operation} failed`, {
        url,
        status: res.status,
        body: res.data,
      });
      throw new Error(`(depop) ${operation} Failed (status ${res.status})`);
    }

    return res.data;
  }

  async getReceipts({ cursor = "", limit = 100 } = {}) {
    const url = `${DepopApiBase}/api/v1/receipts/?role=seller&limit=${limit}&cursor=${encodeURIComponent(
      cursor
    )}`;
    return await this.request("getReceipts", url);
  }

  /**
   * Iterates receipts pages. Stops when `meta.end === true` or there is no
   * `meta.cursor`, using `meta.cursor` as the next page's cursor parameter.
   */
  async *getReceiptsFull() {
    let cursor = "";
    for (;;) {
      const res = await this.getReceipts({ cursor });
      const receipts = res?.receipts || [];
      yield receipts;

      const meta = res?.meta;
      if (!meta || meta.end === true || !meta.cursor) break;
      cursor = meta.cursor;
    }
  }

  async fetchReceiptDetail(purchaseId: string | number) {
    const url = `${DepopApiBase}/api/v1/receipts/${purchaseId}/?country=us&lang=en&role=seller`;
    return await this.request("fetchReceiptDetail", url);
  }

  async getProducts({ cursor = "", limit = 100 } = {}) {
    const url = `${DepopApiBase}/api/v1/shop/products/?lang=en&cursor=${encodeURIComponent(
      cursor
    )}&limit=${limit}&statusFilter=selling&showProductInsights=true`;
    return await this.request("getProducts", url);
  }

  async getProductCounts() {
    const url = `${DepopApiBase}/presentation/api/v1/users/me/product-counts/`;
    return await this.request("getProductCounts", url);
  }

  /**
   * Iterates shop product pages. Same stop condition as receipts.
   */
  async *getProductsFull() {
    let cursor = "";
    for (;;) {
      const res = await this.getProducts({ cursor });
      const products = res?.products || [];
      yield products;

      const meta = res?.meta;
      if (!meta || meta.end === true || !meta.cursor) break;
      cursor = meta.cursor;
    }
  }

  async fetchProductBySlug(slug: string) {
    const url = `${DepopApiBase}/presentation/api/v1/products/by-slug/${slug}/`;
    return await this.request("fetchProductBySlug", url);
  }

  async fetchProductBySlugEditListing(slug: string) {
    const url = `${DepopApiBase}/presentation/api/v1/products/by-slug/${slug}/edit-listing/`;
    return await this.request("fetchProductBySlugEditListing", url);
  }

  async fetchAttributes() {
    const url = `${DepopApiBase}/presentation/api/v1/attributes/`;
    return await this.request("fetchAttributes", url);
  }
}

export default DepopClient;
